// Docs: https://www.instantdb.com/docs/modeling-data

import { i } from "@instantdb/react";

const _schema = i.schema({
	entities: {
		$files: i.entity({
			path: i.string().unique().indexed(),
			url: i.string(),
		}),
		$users: i.entity({
			email: i.string().unique().indexed().optional(),
			imageURL: i.string().optional(),
			type: i.string().optional(),
		}),
		accounts: i.entity({
			accessToken: i.string().optional(),
			accessTokenExpiresAt: i.date().optional(),
			accountId: i.string().indexed(),
			createdAt: i.date(),
			idToken: i.string().optional(),
			password: i.string().optional(),
			providerId: i.string().indexed(),
			refreshToken: i.string().optional(),
			refreshTokenExpiresAt: i.date().optional(),
			scope: i.string().optional(),
			updatedAt: i.date(),
			userId: i.string().indexed(),
		}),
		sessions: i.entity({
			createdAt: i.date(),
			expiresAt: i.date(),
			impersonatedBy: i.string().optional(),
			ipAddress: i.string().optional(),
			token: i.string().unique().indexed(),
			updatedAt: i.date(),
			userAgent: i.string().optional(),
			userId: i.string().indexed(),
		}),
		subscriptions: i.entity({
			cancelAtPeriodEnd: i.boolean().optional(),
			periodEnd: i.date().optional(),
			periodStart: i.date().optional(),
			plan: i.string().indexed(),
			referenceId: i.string().indexed(),
			seats: i.number().optional(),
			status: i.string().indexed().optional(),
			stripeCustomerId: i.string().indexed().optional(),
			stripeSubscriptionId: i.string().indexed().optional(),
		}),
		users: i.entity({
			apiCallsUsed: i.number().optional(),
			apiCallsWindowStart: i.date().optional(),
			// Legacy plaintext key; superseded by apiKeyHash + apiKeyPrefix.
			apiKey: i.string().unique().indexed().optional(),
			// SHA-256 hash of the API key (the stored verifier) + a short non-secret
			// display prefix. The raw key is shown to the user only once at mint time.
			apiKeyHash: i.string().unique().indexed().optional(),
			apiKeyPrefix: i.string().optional(),
			banned: i.boolean().optional(),
			banExpires: i.date().optional(),
			banReason: i.string().optional(),
			createdAt: i.date(),
			email: i.string().unique().indexed(),
			emailVerified: i.boolean().optional(),
			image: i.string().optional(),
			mrrUpdatesEnabled: i.boolean().optional(),
			name: i.string().indexed().optional(),
			onboardedAt: i.date().optional(),
			role: i.string().optional(),
			signupMethod: i.string().indexed().optional(),
			stripeCustomerId: i.string().indexed().optional(),
			updatedAt: i.date(),
		}),
		founders: i.entity({
			avatarUrl: i.string().optional(),
			bio: i.string().optional(),
			country: i.string().indexed().optional(),
			createdAt: i.date(),
			githubUrl: i.string().optional(),
			linkedinUrl: i.string().optional(),
			name: i.string().indexed(),
			slug: i.string().unique().indexed(),
			storyBio: i.string().optional(),
			storyNext: i.string().optional(),
			storyStack: i.string().optional(),
			storyWorkingStyle: i.string().optional(),
			tagline: i.string().optional(),
			updatedAt: i.date(),
			website: i.string().optional(),
			xFollowersCount: i.number().indexed().optional(),
			xHandle: i.string().indexed().optional(),
		}),
		agents: i.entity({
			allTimeRevenue: i.number().indexed(),
			categories: i.json().optional(),
			country: i.string().indexed().optional(),
			currency: i.string().indexed(),
			createdAt: i.date(),
			currentActiveSubscriptions: i.number().indexed(),
			description: i.string(),
			estimatedMrr: i.number().indexed(),
			firstChargeDate: i.date().indexed().optional(),
			foundedAt: i.date(),
			founderName: i.string().indexed(),
			founderMessage: i.string().optional(),
			founderXHandle: i.string().indexed().optional(),
			isPublic: i.boolean().indexed(),
			isStealth: i.boolean().indexed().optional(),
			lastSyncedAt: i.date().indexed().optional(),
			lastVerifiedAt: i.date().indexed().optional(),
			momMrrChangePct: i.number().optional(),
			momRevenueChangePct: i.number().optional(),
			name: i.string().indexed(),
			publishedAt: i.date().indexed().optional(),
			listingError: i.string().optional(),
			listingSource: i.string().indexed().optional(),
			listingStatus: i.string().indexed().optional(),
			logoUrl: i.string().optional(),
			profitMarginPct: i.number().optional(),
			slug: i.string().unique().indexed(),
			updatedAt: i.date(),
			agentInsights: i.string().optional(),
			verificationDisplayLabel: i.string().optional(),
			verificationProvider: i.string().indexed().optional(),
			verificationStatus: i.string().indexed().optional(),
			websiteVerificationError: i.string().optional(),
			websiteVerificationStatus: i.string().indexed().optional(),
			websiteVerificationToken: i.string().indexed().optional(),
			websiteVerifiedAt: i.date().indexed().optional(),
			trailingChurnRate: i.number().optional(),
			website: i.string(),
		}),
		agentRevenueSnapshots: i.entity({
			activeSubscriptions: i.number().indexed().optional(),
			agentIsPublic: i.boolean().indexed(),
			agentOwnerId: i.string().indexed(),
			chargeCount: i.number().indexed().optional(),
			churnRate: i.number().indexed().optional(),
			createdAt: i.date(),
			currency: i.string().indexed(),
			estimatedMrr: i.number().indexed(),
			grossRevenue: i.number().indexed(),
			snapshotDate: i.date().indexed(),
			source: i.string().indexed(),
		}),
		agentRevenueConnections: i.entity({
			createdAt: i.date(),
			encryptedSecret: i.string(),
			keyFingerprint: i.string().indexed(),
			keyPrefix: i.string().indexed(),
			lastSyncAt: i.date().indexed().optional(),
			lastSyncError: i.string().optional(),
			lastSyncStatusAt: i.date().indexed().optional(),
			provider: i.string().indexed(),
			status: i.string().indexed(),
			updatedAt: i.date(),
			verificationLabel: i.string().optional(),
		}),
		contentPosts: i.entity({
			contentBrief: i.string(),
			createdAt: i.date(),
			errorMessage: i.string().optional(),
			generatedAssetId: i.string().indexed().optional(),
			generatedAssetUrl: i.string().optional(),
			generatedMetadata: i.string().optional(),
			generatedText: i.string().optional(),
			generationModel: i.string().optional(),
			generationProvider: i.string().optional(),
			ownerKey: i.string().indexed(),
			platformOverrides: i.string().optional(),
			platforms: i.string(),
			postType: i.string().indexed(),
			prompt: i.string().optional(),
			publishedAt: i.date().optional(),
			scheduledAt: i.date().optional(),
			status: i.string().indexed(),
			updatedAt: i.date(),
		}),
		contentPostDeliveries: i.entity({
			createdAt: i.date(),
			errorMessage: i.string().optional(),
			legacyPublicationId: i.string().indexed().optional(),
			mediaAssetId: i.string().indexed().optional(),
			mediaType: i.string().indexed(),
			mediaUrl: i.string().optional(),
			provider: i.string().indexed(),
			publishedAt: i.date().optional(),
			remoteContainerId: i.string().indexed().optional(),
			remoteMediaId: i.string().indexed().optional(),
			scheduledAt: i.date().optional(),
			status: i.string().indexed(),
			text: i.string().optional(),
			updatedAt: i.date(),
		}),
		verifications: i.entity({
			createdAt: i.date().indexed().optional(),
			expiresAt: i.date().indexed(),
			identifier: i.string().indexed(),
			updatedAt: i.date().optional(),
			value: i.string(),
		}),
		feedback: i.entity({
			category: i.string().indexed().optional(),
			contactEmail: i.string().optional(),
			createdAt: i.date(),
			message: i.string(),
			userEmail: i.string().optional(),
			userName: i.string().optional(),
		}),
		ads: i.entity({
			activatedAt: i.date().indexed().optional(),
			amountCents: i.number().indexed().optional(),
			createdAt: i.date().indexed(),
			customerEmail: i.string().indexed().optional(),
			deletedAt: i.date().indexed().optional(),
			description: i.string().optional(),
			expiresAt: i.date().indexed().optional(),
			image: i.string().optional(),
			orderNo: i.string().indexed().optional(),
			position: i.number().unique().indexed().optional(),
			status: i.number().indexed(),
			stripeCheckoutSessionId: i.string().unique().indexed().optional(),
			stripeCustomerId: i.string().indexed().optional(),
			stripeSubscriptionId: i.string().unique().indexed().optional(),
			title: i.string().indexed(),
			updatedAt: i.date().indexed(),
			url: i.string(),
		}),
		orders: i.entity({
			amount: i.number().indexed().optional(),
			checkoutInfo: i.string().optional(),
			checkoutResult: i.string().optional(),
			checkoutUrl: i.string().optional(),
			createdAt: i.date().indexed(),
			currency: i.string().optional(),
			orderNo: i.string().unique().indexed(),
			paidAt: i.date().optional(),
			paymentAmount: i.number().optional(),
			paymentCurrency: i.string().optional(),
			paymentEmail: i.string().optional(),
			paymentProvider: i.string().optional(),
			paymentResult: i.string().optional(),
			paymentSessionId: i.string().optional(),
			paymentType: i.string().indexed().optional(),
			productName: i.string().optional(),
			status: i.string().indexed(),
			updatedAt: i.date().indexed(),
		}),
		digestSubscribers: i.entity({
			email: i.string().unique().indexed(),
			unsubscribeToken: i.string().unique().indexed(),
			subscribedAt: i.date(),
			unsubscribedAt: i.date().optional(),
			isActive: i.boolean().indexed(),
			source: i.string().indexed(),
		}),
		agentMilestoneEvents: i.entity({
			agentId: i.string().indexed(),
			agentOwnerId: i.string().indexed(),
			milestone: i.number().indexed(),
			crossedAt: i.date().indexed(),
			seenAt: i.date().indexed().optional(),
		}),
	},
	links: {
		$usersLinkedPrimaryUser: {
			forward: {
				on: "$users",
				has: "one",
				label: "linkedPrimaryUser",
				onDelete: "cascade",
			},
			reverse: {
				on: "$users",
				has: "many",
				label: "linkedGuestUsers",
			},
		},
		accountsUser: {
			forward: {
				on: "accounts",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "accounts",
			},
		},
		sessionsUser: {
			forward: {
				on: "sessions",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "sessions",
			},
		},
		subscriptionsUser: {
			forward: {
				on: "subscriptions",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "subscriptions",
			},
		},
		agentsUser: {
			forward: {
				on: "agents",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "agents",
			},
		},
		foundersUser: {
			forward: {
				on: "founders",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "one",
				label: "founder",
			},
		},
		agentsFounder: {
			forward: {
				on: "agents",
				has: "one",
				label: "founder",
			},
			reverse: {
				on: "founders",
				has: "many",
				label: "agents",
			},
		},
		agentRevenueSnapshotsAgent: {
			forward: {
				on: "agentRevenueSnapshots",
				has: "one",
				label: "agent",
				onDelete: "cascade",
			},
			reverse: {
				on: "agents",
				has: "many",
				label: "revenueSnapshots",
			},
		},
		agentRevenueConnectionsAgent: {
			forward: {
				on: "agentRevenueConnections",
				has: "one",
				label: "agent",
				onDelete: "cascade",
			},
			reverse: {
				on: "agents",
				has: "many",
				label: "revenueConnections",
			},
		},
		contentPostsUser: {
			forward: {
				on: "contentPosts",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "contentPosts",
			},
		},
		contentPostDeliveriesPost: {
			forward: {
				on: "contentPostDeliveries",
				has: "one",
				label: "post",
				onDelete: "cascade",
			},
			reverse: {
				on: "contentPosts",
				has: "many",
				label: "deliveries",
			},
		},
		users$user: {
			forward: {
				on: "users",
				has: "one",
				label: "$user",
				onDelete: "cascade",
			},
			reverse: {
				on: "$users",
				has: "one",
				label: "user",
			},
		},
		feedbackUser: {
			forward: {
				on: "feedback",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "feedback",
			},
		},
		adsUser: {
			forward: {
				on: "ads",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "ads",
			},
		},
		ordersUser: {
			forward: {
				on: "orders",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "orders",
			},
		},
		agentMilestoneEventsAgent: {
			forward: {
				on: "agentMilestoneEvents",
				has: "one",
				label: "agent",
				onDelete: "cascade",
			},
			reverse: {
				on: "agents",
				has: "many",
				label: "milestoneEvents",
			},
		},
	},
	rooms: {},
});

type _AppSchema = typeof _schema;
interface AppSchema extends _AppSchema {}
const schema: AppSchema = _schema;

export type { AppSchema };
export default schema;
